<script setup>
import {onBeforeUnmount, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import {useI18n} from '@/i18n'

const {languages, currentLanguage, languageMeta, setLanguage, t} = useI18n()
const route = useRoute()
const menuOpen = ref(false)
const languageOpen = ref(false)
const isActive = (path) => path === '/' ? route.path === '/' : route.path.startsWith(path)

const navItems = [
  {path: '/', key: 'nav.home'},
  {
    path: '/products',
    key: 'nav.products',
    children: [
      {path: '/products?category_id_category=Steel%20Structure', key: 'static.products.steelStructure'},
      {path: '/products?category_id_category=Steel%20House', key: 'static.products.steelHouse'}
    ]
  },
  {path: '/projects', key: 'nav.projects'},
  {path: '/videos', key: 'nav.videos'},
  {path: '/3d-models', key: 'marketing.models.nav'},
  {path: '/about', key: 'nav.about'},
  {path: '/blogs', key: 'nav.blogs'},
  {path: '/contact', key: 'nav.contact'}
]

function closeMenus() {
  menuOpen.value = false
  languageOpen.value = false
}

function handleEscape(event) {
  if (event.key === 'Escape') closeMenus()
}

watch(() => route.path, closeMenus)
document.addEventListener('keydown', handleEscape)
onBeforeUnmount(() => document.removeEventListener('keydown', handleEscape))
</script>

<template>
  <header class="mkt-header">
    <div class="mkt-header-utility">
      <div class="mkt-shell mkt-header-utility-inner">
        <span><i class="mdi mdi-earth" aria-hidden="true"></i>{{ t('marketing.footer.availability') }}</span>
        <div>
          <a href="tel:+8613929160928"><i class="mdi mdi-phone" aria-hidden="true"></i>+86 139 2916 0928</a>
          <a href="mailto:ray.chan@tpa-prefabhouse.com"><i class="mdi mdi-email-outline" aria-hidden="true"></i>ray.chan@tpa-prefabhouse.com</a>
        </div>
      </div>
    </div>
    <div class="mkt-header-main">
      <div class="mkt-shell mkt-header-main-inner">
        <a class="mkt-brand" href="/" aria-label="TPA Housing">
          <img src="/images/tpa2.png" alt="" width="82" height="36">
          <span><strong>TPA</strong> HOUSING<small>STEEL BUILDING SYSTEMS</small></span>
        </a>

        <nav class="mkt-desktop-nav" aria-label="Primary navigation">
          <div v-for="item in navItems" :key="item.path" class="mkt-nav-item"
               :class="{'has-dropdown': item.children}">
            <a :href="item.path" :class="{active: isActive(item.path)}"
               :aria-haspopup="item.children ? 'true' : undefined">
              {{ t(item.key) }}
              <i v-if="item.children" class="mdi mdi-chevron-down" aria-hidden="true"></i>
            </a>
            <div v-if="item.children" class="mkt-nav-dropdown">
              <a v-for="child in item.children" :key="child.path" :href="child.path">
                {{ t(child.key) }}<i class="mdi mdi-arrow-right" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </nav>

        <div class="mkt-header-actions">
          <div class="mkt-language">
            <button type="button" class="mkt-icon-button mkt-language-trigger"
                    :aria-expanded="languageOpen" :aria-label="t('common.language')"
                    @click="languageOpen = !languageOpen">
              <i class="mdi mdi-web" aria-hidden="true"></i><span>{{ languageMeta.short }}</span>
            </button>
            <div v-if="languageOpen" class="mkt-language-panel">
              <button v-for="language in languages" :key="language.code" type="button"
                      :class="{active: currentLanguage === language.code}" @click="setLanguage(language.code)">
                <span>{{ language.short }}</span>{{ language.label }}
              </button>
            </div>
          </div>
          <a class="mkt-button mkt-button-primary mkt-header-quote" href="/contact">
            {{ t('marketing.shared.quote') }}
          </a>
          <button type="button" class="mkt-icon-button mkt-menu-toggle"
                  :aria-expanded="menuOpen" :aria-label="menuOpen ? t('marketing.shared.close') : t('marketing.shared.menu')"
                  @click="menuOpen = !menuOpen">
            <i class="mdi" :class="menuOpen ? 'mdi-close' : 'mdi-menu'" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-if="menuOpen" class="mkt-mobile-menu">
      <nav class="mkt-shell" aria-label="Mobile navigation">
        <template v-for="item in navItems" :key="item.path">
          <a :href="item.path" :class="{active: isActive(item.path)}">
            {{ t(item.key) }}<i class="mdi mdi-chevron-right" aria-hidden="true"></i>
          </a>
          <div v-if="item.children" class="mkt-mobile-submenu">
            <a v-for="child in item.children" :key="child.path" :href="child.path">
              {{ t(child.key) }}
            </a>
          </div>
        </template>
        <a class="mkt-button mkt-button-primary" href="/contact">{{ t('marketing.shared.quote') }}</a>
      </nav>
    </div>
  </header>
</template>
